import { useQuery } from '@apollo/client/react'
import {
  AssociationResult,
  ErrorState,
  LoadingState,
  MatchingResult,
  Notice,
  RichText,
  ReviewQuestion,
  ScoreIndicator,
  cn,
  linkClasses,
  useLanguage,
  useTranslate,
  PANEL_SURFACE,
} from '@athena/ui'
import { Link } from '@tanstack/react-router'

import { usePageTitle } from '../../app/page-title.js'
import { AttemptReviewDocument } from '../../graphql/generated/operations.js'
import { apiErrorCode } from '../../lib/errors.js'
import { useFormat } from '../../lib/format.js'

export interface AttemptReviewProps {
  attemptId: string
}

/**
 * One finished attempt, with the student's answers and the answer key.
 *
 * This is the administrative view, so it shows what a student's own results
 * screen must not: the correct option, the accepted answers, the explanation.
 * The whole family behind it is reachable only from `attemptReview`, and
 * `schema.test.ts` walks the student's roots to prove they cannot get here.
 */
export function AttemptReview({ attemptId }: AttemptReviewProps) {
  const t = useTranslate()
  const language = useLanguage()
  const format = useFormat()
  const { data, loading, error } = useQuery(AttemptReviewDocument, { variables: { attemptId } })

  usePageTitle(data?.attemptReview.practiceTitle ?? t('attempt.title'))

  if (loading) return <LoadingState>{t('attempt.loading')}</LoadingState>
  // Refused, not failed: an earlier attempt on a plan without detailed
  // history. Said as what it is, with the score's whereabouts, not as an
  // error to retry.
  if (error && apiErrorCode(error) === 'FORBIDDEN') {
    return <Notice tone="info" title={t('attempt.reviewNotIncluded')} />
  }
  if (error) return <ErrorState>{t('attempt.loadError')}</ErrorState>
  if (!data) return null

  const review = data.attemptReview

  return (
    <div className="mx-auto flex w-full min-w-0 max-w-5xl flex-col gap-6 sm:gap-8">
      <div className={cn(PANEL_SURFACE, 'flex flex-wrap items-start justify-between gap-6')}>
        <div className="flex flex-col gap-2">
          <h2 className="text-h2 font-semibold text-text text-balance">{review.practiceTitle}</h2>
          <p className="text-body-sm text-text-secondary">
            <Link
              to="/$language/students/$studentId"
              params={{ language, studentId: review.studentId }}
              className={linkClasses()}
            >
              {review.studentName}
            </Link>
            {' · '}
            {format.dateTime(review.submittedAt)}
          </p>
        </div>

        <ScoreIndicator
          correctCount={review.correctCount}
          questionCount={review.questionCount}
          detail={format.percent(review.percent)}
        />
      </div>

      <ol className="divide-y divide-border">
        {review.questions.map((question) => (
          <li key={question.id}>
            <ReviewQuestion
              number={question.position + 1}
              prompt={<RichText inline>{question.prompt}</RichText>}
              isCorrect={question.isCorrect}
              resultLabel={
                /* A matching question is worth a point per pair, so the badge
                   says how many of them were earned rather than pretending the
                   answer was one thing. */
                question.outOf > 1
                  ? t('attempt.tutorReview.credit', [question.credit, question.outOf])
                  : question.isCorrect
                    ? t('common.correct')
                    : t('common.incorrect')
              }
              answer={
                question.type === 'MATCHING' ? (
                  <MatchingResult
                    rows={question.matches.map((row) => ({
                      leftText: row.leftText,
                      yourAnswer: row.selectedRightText,
                      // Shown on every row, right or wrong: unlike the student's
                      // view this one is for the person who wrote the question.
                      correctAnswer: row.correctRightText,
                      isCorrect: row.isCorrect,
                    }))}
                    yourLabel={t('attempt.tutorReview.studentPut', [review.studentName])}
                    correctLabel={t('attempt.tutorReview.matches')}
                  />
                ) : question.type === 'ASSOCIATION' ? (
                  <AssociationResult
                    yourOrder={textsFor(question, question.submittedOrder)}
                    correctOrder={textsFor(question, question.correctOrder)}
                    yourLabel={t('attempt.tutorReview.studentsAnswer', [review.studentName])}
                    correctLabel={t('attempt.tutorReview.correctOrder')}
                  />
                ) : question.options.length > 0 ? (
                  <ul className="flex flex-col gap-2">
                    {question.options.map((option) => {
                      const chosen = option.id === question.selectedOptionId
                      return (
                        <li
                          key={option.id}
                          className={cn(
                            'flex flex-wrap items-center gap-3 rounded-sm border px-4 py-2 text-body',
                            option.isCorrect
                              ? 'border-accent-border bg-accent-light text-text'
                              : 'border-border bg-surface text-text-secondary',
                          )}
                        >
                          <span className="font-semibold">{option.label}.</span>
                          <span>
                            <RichText inline>{option.text}</RichText>
                          </span>
                          <span className="ml-auto flex items-center gap-3 text-body-sm font-semibold">
                            {chosen ? (
                              <span className="text-text-strong">
                                {t('attempt.tutorReview.choseThis', [review.studentName])}
                              </span>
                            ) : null}
                            {option.isCorrect ? (
                              <span className="text-accent-text">
                                {t('attempt.tutorReview.correctAnswer')}
                              </span>
                            ) : null}
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                ) : (
                  <div className="flex flex-col gap-1 text-body">
                    <p>
                      <span className="font-semibold text-text-strong">
                        {t('attempt.tutorReview.theirAnswer')}
                      </span>
                      {question.textAnswer === null || question.textAnswer === '' ? (
                        <span className="text-text-secondary">
                          {t('attempt.tutorReview.leftBlank')}
                        </span>
                      ) : (
                        question.textAnswer
                      )}
                    </p>
                    <p>
                      <span className="font-semibold text-accent-text">
                        {t('attempt.tutorReview.accepted')}
                      </span>
                      <RichText inline>{question.acceptedAnswers.join(' · ')}</RichText>
                    </p>
                  </div>
                )
              }
              explanation={
                question.explanation ? (
                  <RichText inline>{question.explanation}</RichText>
                ) : undefined
              }
            />
          </li>
        ))}
      </ol>
    </div>
  )
}

/**
 * Option ids to their text, for the two orders an ordering question carries.
 *
 * The tutor's payload sends ids rather than text, because this view already
 * holds the options and repeating every string twice would be a bigger payload
 * saying the same thing.
 */
function textsFor(
  question: { options: ReadonlyArray<{ id: string; text: string }> },
  order: readonly string[],
): string[] {
  const byId = new Map(question.options.map((option) => [option.id, option.text]))
  return order.flatMap((optionId) => {
    const text = byId.get(optionId)
    return text === undefined ? [] : [text]
  })
}
